interface Filters {
  search: string;
  type: string;
  propertyType: string;
  minPrice: string;
  maxPrice: string;
  bedrooms: string;
  city: string;
}

interface FilterBarProps {
  filters: Filters;
  onChange: (filters: Filters) => void;
  onClear: () => void;
  total: number;
}

const FilterBar = ({ filters, onChange, onClear, total }: FilterBarProps) => {
  const update = (key: keyof Filters, value: string) => {
    onChange({ ...filters, [key]: value });
  };

  return (
    <div className="bg-white border-b border-stone-100 sticky top-[72px] z-40">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
        {/* Search Row */}
        <div className="flex flex-col lg:flex-row gap-3 mb-3">
          <div className="flex-1 flex items-center gap-2 bg-stone-50 border border-stone-200 rounded-xl px-4 py-2.5">
            <span className="w-4 h-4 flex items-center justify-center text-stone-400">
              <i className="ri-search-line text-sm"></i>
            </span>
            <input
              type="text"
              value={filters.search}
              onChange={e => update('search', e.target.value)}
              placeholder="Search by title, location, or keyword..."
              className="flex-1 bg-transparent text-sm text-stone-700 placeholder-stone-400 focus:outline-none"
            />
            {filters.search && (
              <button onClick={() => update('search', '')} className="text-stone-400 hover:text-stone-600 cursor-pointer">
                <i className="ri-close-line text-sm"></i>
              </button>
            )}
          </div>
          <button
            onClick={onClear}
            className="px-4 py-2.5 text-stone-500 hover:text-stone-700 text-sm font-medium border border-stone-200 rounded-xl hover:border-stone-300 transition-colors cursor-pointer whitespace-nowrap"
          >
            Clear All
          </button>
        </div>

        {/* Filter Chips Row */}
        <div className="flex flex-wrap gap-2 items-center">
          {/* Listing Type */}
          <div className="flex items-center gap-1 bg-stone-50 border border-stone-200 rounded-xl px-1 py-1">
            {[
              { label: 'All', value: '' },
              { label: 'Buy', value: 'sale' },
              { label: 'Rent', value: 'rent' },
            ].map(opt => (
              <button
                key={opt.value}
                onClick={() => update('type', opt.value)}
                className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                  filters.type === opt.value
                    ? 'bg-amber-500 text-white'
                    : 'text-stone-500 hover:text-stone-700'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Property Type */}
          <select
            value={filters.propertyType}
            onChange={e => update('propertyType', e.target.value)}
            className="bg-stone-50 border border-stone-200 rounded-xl px-3 py-2 text-xs text-stone-600 focus:outline-none focus:border-amber-400 cursor-pointer"
          >
            <option value="">All Types</option>
            {['House', 'Apartment', 'Villa', 'Studio', 'Penthouse', 'Townhouse'].map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>

          {/* City */}
          <select
            value={filters.city}
            onChange={e => update('city', e.target.value)}
            className="bg-stone-50 border border-stone-200 rounded-xl px-3 py-2 text-xs text-stone-600 focus:outline-none focus:border-amber-400 cursor-pointer"
          >
            <option value="">All Cities</option>
            {['Beverly Hills', 'Manhattan', 'London', 'Miami', 'New York', 'Westchester', 'Malibu', 'Los Angeles'].map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>

          {/* Bedrooms */}
          <select
            value={filters.bedrooms}
            onChange={e => update('bedrooms', e.target.value)}
            className="bg-stone-50 border border-stone-200 rounded-xl px-3 py-2 text-xs text-stone-600 focus:outline-none focus:border-amber-400 cursor-pointer"
          >
            <option value="">Any Beds</option>
            {['1', '2', '3', '4', '5', '6'].map(n => (
              <option key={n} value={n}>{n}+ Beds</option>
            ))}
          </select>

          {/* Price Range */}
          <select
            value={filters.minPrice}
            onChange={e => update('minPrice', e.target.value)}
            className="bg-stone-50 border border-stone-200 rounded-xl px-3 py-2 text-xs text-stone-600 focus:outline-none focus:border-amber-400 cursor-pointer"
          >
            <option value="">Min Price</option>
            <option value="500000">$500K</option>
            <option value="750000">$750K</option>
            <option value="1000000">$1M</option>
            <option value="1500000">$1.5M</option>
          </select>

          <select
            value={filters.maxPrice}
            onChange={e => update('maxPrice', e.target.value)}
            className="bg-stone-50 border border-stone-200 rounded-xl px-3 py-2 text-xs text-stone-600 focus:outline-none focus:border-amber-400 cursor-pointer"
          >
            <option value="">Max Price</option>
            <option value="500000">$500K</option>
            <option value="1000000">$1M</option>
            <option value="1500000">$1.5M</option>
            <option value="2000000">$2M</option>
          </select>

          <span className="ml-auto text-stone-400 text-xs whitespace-nowrap">
            <strong className="text-stone-700">{total}</strong> properties found
          </span>
        </div>
      </div>
    </div>
  );
};

export default FilterBar;
